import React from 'react';
import Box from '@mui/material/Box';

import { GetServerSidePropsContext } from 'next/types';
import { ILandingPageProps } from '../../../interfaces/IPages';
import { IShip } from '../../../interfaces/ship/IShip';

import { HeadTags } from 'src/components/sections/head-tags/head-tags';
import { CabinType } from 'src/components/sections/cabin-type/cabin-type';
import { DeckPlan } from 'src/components/sections/deck-plan/deck-plan';
import { SubpageStickyBar } from 'src/components/molecules/subpage-sticky-bar/subpage-sticky-bar';
import { Breadcrumbs } from 'src/components/atoms/breadcrumbs/breadcrumbs';
import { StyledBreadcrumbsContainer } from 'src/components/atoms/breadcrumbs/breadcrumbs.style';
import { getBreadcrumbsPath } from 'src/components/sections/landing-breadcrumbs/landing-breadcrumbs';

import { LocaleConfigContext } from 'src/contexts/locale-config/locale-config';
import { getShipPageData } from 'src/helpers/ship-page/getShipPageData';
import { __ } from 'src/utils/translation-service';
import { useHandleSearchSubmit } from 'src/hooks/useHandleSearchSubmit';
import { ssrRedirectToErrorPage } from 'src/config/ssrRedirectToErrorPage';
import { getSsrRedirectTo } from '../../../src/helpers/getSSRRedirectTo';

const ShipCabinPage = (props: ILandingPageProps) => {
    const localeConfig = React.useContext(LocaleConfigContext);

    const modulesData = props?.pageProps?.modulesData;
    const landingPage = props?.pageProps?.getLandingPage;
    const meta = props?.pageProps?.getLandingPage?.meta;

    const breadcrumbsPath = getBreadcrumbsPath(landingPage, modulesData, localeConfig);

    const hostUrl = props?.hostUrl;

    const shipPageUrl = `/${localeConfig.slugs.ship.prefix}/${landingPage.slug}`;

    const headTagsData = {
        ...meta,
        hostUrl,
        landingPage,
        modulesData,
        pageType: 'landingPage',
    };

    const subPageObserverRef = React.useRef(null);

    const handleSubmit = useHandleSearchSubmit({
        id: landingPage.id,
        type: landingPage.type,
        title: landingPage.title,
    });

    if (!modulesData || !modulesData[`ship${landingPage.id}`]) {
        return null;
    }

    const ship = modulesData[`ship${landingPage.id}`];

    if (!ship || !ship.decks) {
        return null;
    }

    const cabinDescription = ship?.description?.cabins || '';

    const cabinInfoByType = ship.cabinInfoByType;

    return (
        <>
            <HeadTags {...headTagsData} />

            <div ref={subPageObserverRef}>
                <SubpageStickyBar
                    observerRef={subPageObserverRef}
                    shipUrl={shipPageUrl}
                    onSearchSubmit={handleSubmit}
                />
            </div>
            <>
                <CabinType
                    headline={`${ship.title} ${__('subpageTitleCabin')}`}
                    description={cabinDescription}
                    cabinTypes={cabinInfoByType}
                    decks={ship.decks}
                />
                <Box marginBottom={'140px'} />
                <DeckPlan key="deckPlan" decks={ship.decks} title={`${__('deckPlan')}`} />
            </>
            <StyledBreadcrumbsContainer>
                <Breadcrumbs paths={breadcrumbsPath} />
            </StyledBreadcrumbsContainer>
        </>
    );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const [, pageType, slug, slugSubpage] = context.resolvedUrl.split('/');

    const pageData = await getShipPageData({ slug, pageType, slugSubpage });

    if (!pageData) {
        return ssrRedirectToErrorPage;
    }

    const { landingPage, modulesData, configData } = pageData;

    const shipData = modulesData[`ship${landingPage.id}`] as IShip;

    if (!shipData?.decks) {
        return getSsrRedirectTo(landingPage.url);
    }

    return {
        props: {
            getLandingPage: landingPage,
            modulesData: modulesData,
            slug,
            globalConfigData: configData, // Used in the app as globalConfigData
        },
    };
}

export default ShipCabinPage;
